import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import { authService } from '../services/authService';
import DatabaseService from '../services/databaseService';
import { createClient } from '@supabase/supabase-js';

// Get Supabase environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if we have valid Supabase configuration
const hasValidSupabaseConfig = supabaseUrl && supabaseUrl !== 'https://placeholder.supabase.co' &&
                               supabaseAnonKey && supabaseAnonKey !== 'placeholder-anon-key';

// Initialize Supabase client only if we have valid config
let supabase = null;

if (hasValidSupabaseConfig) {
  supabase = createClient(supabaseUrl, supabaseAnonKey);
  console.log('SUCCESS: Supabase client initialized successfully');
} else {
  console.warn('WARNING: Supabase not configured in AuthContext. Environment variables missing.');
  console.log('Current environment variables:', {
    VITE_SUPABASE_URL: supabaseUrl,
    VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? 'Present' : 'Missing'
  });
}

// START: DEVELOPMENT MODE: Bypass authentication
const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true' || false; // Set to false to enable authentication

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // START: DEVELOPMENT MODE: Auto-authenticate with default user
      if (BYPASS_AUTH) {
        console.log('START: DEVELOPMENT MODE: Bypassing authentication');

        // Check if user was previously set (from login)
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
            setLoading(false);
            console.log('SUCCESS: Development user authenticated from storage:', parsedUser.name, parsedUser.role);
            return;
          } catch (e) {
            console.warn('Failed to parse stored user, using default');
          }
        }

        // Default SME user for AIMS if no stored user
        const defaultUser = {
          id: 'dev-sme',
          name: 'SME Admin (AIMS)',
          email: 'sme@aims.com',
          role: 'super_admin',
          profilePicture: null,
          isActivated: true,
          clinicId: null
        };

        setUser(defaultUser);
        setIsAuthenticated(true);
        setLoading(false);
        localStorage.setItem('user', JSON.stringify(defaultUser));
        console.log('SUCCESS: Development user authenticated:', defaultUser.name, defaultUser.role);
        return;
      }

      // PRODUCTION MODE: Check for stored authentication
      // First check localStorage for token and user
      const storedToken = localStorage.getItem('authToken') || Cookies.get('authToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          console.log('DEBUG: Found stored auth token and user:', parsedUser.email, parsedUser.role);

          // Restore user from localStorage
          setUser(parsedUser);
          setIsAuthenticated(true);
          console.log('SUCCESS: User authenticated from localStorage:', parsedUser.name, parsedUser.role);

          // Optionally verify token with backend or Supabase in background
          // but don't block the UI
          setLoading(false);
          return;
        } catch (e) {
          console.warn('Failed to parse stored user:', e);
        }
      }

      // If no localStorage auth, check Supabase session
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          // Get user profile from database
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          const userData = {
            id: session.user.id,
            email: session.user.email,
            name: profile?.full_name || session.user.user_metadata?.full_name || 'User',
            role: profile?.role || session.user.user_metadata?.role || 'patient',
            avatar: profile?.avatar_url,
            isActivated: true
          };

          setUser(userData);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(userData));
          console.log('SUCCESS: User authenticated from Supabase session:', userData.name, userData.role);
        } else {
          console.log('ERROR: No active session found');
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        console.log('ERROR: No stored auth and Supabase not available');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.warn('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials, method = 'email') => {
    console.log('START: AuthContext: Starting login process');

    // START: DEVELOPMENT MODE: Auto-succeed login
    if (BYPASS_AUTH) {
      console.log('START: DEVELOPMENT MODE: Bypassing login authentication');
      setLoading(true);

      // Simulate loading time
      await new Promise(resolve => setTimeout(resolve, 500));

      // Determine user role based on email patterns
      const email = credentials.email || 'dev@aims.com';
      let userRole = 'super_admin'; // default
      let userName = 'Development User';
      let clinicId = null;

      // Role detection based on email patterns
      if (email.includes('superadmin') || email.includes('admin@aims')) {
        userRole = 'super_admin';
        userName = 'Super Admin (Dev)';
        clinicId = null;
      } else if (email.includes('clinic') || email.includes('@clinic') ||
                 email.includes('doctor') || email.includes('dr.')) {
        userRole = 'clinic_admin';
        userName = 'Engineer (Dev)';
        clinicId = 'dev-clinic-123';
      } else if (email.includes('patient')) {
        userRole = 'patient';
        userName = 'Supervisor (Dev)';
        clinicId = 'dev-clinic-123';
      }

      const defaultUser = {
        id: `dev-${userRole}-${Date.now()}`,
        name: userName,
        email: email,
        role: userRole,
        profilePicture: null,
        isActivated: true,
        clinicId: clinicId
      };

      setUser(defaultUser);
      setIsAuthenticated(true);
      setLoading(false);
      localStorage.setItem('user', JSON.stringify(defaultUser));
      localStorage.setItem('authToken', 'dev-bypass-token');

      toast.success(`START: Development mode login as ${userRole}!`);
      console.log('SUCCESS: Development login successful:', defaultUser.name, defaultUser.role);

      return {
        success: true,
        user: defaultUser,
        message: 'Development mode login successful'
      };
    }

    try {
      setLoading(true);
      let response;
      
      console.log('EMAIL: Login method:', method);
      console.log('NOTE: Credentials:', { email: credentials.email, hasPassword: !!credentials.password });
      
      switch (method) {
        case 'email':
          response = await authService.loginWithEmail(credentials);
          break;
        case 'google':
          response = await authService.loginWithGoogle();
          break;
        case 'github':
          response = await authService.loginWithGitHub();
          break;
        case 'facebook':
          response = await authService.loginWithFacebook();
          break;
        default:
          throw new Error('Invalid authentication method');
      }

      console.log(' Auth response:', response);

      if (response && response.success && response.token) {
        // Store authentication data in multiple places for reliability
        Cookies.set('authToken', response.token, { expires: 7 }); // 7 days
        localStorage.setItem('authToken', response.token);
        
        // Fetch the latest user data from database to get updated profile picture
        let latestUserData = response.user;
        try {
          console.log('REFRESH: Fetching latest user data from database...');
          if (response.user.role === 'super_admin') {
            const superAdminData = await DatabaseService.findById('superAdmins', response.user.id);
            if (superAdminData) {
              latestUserData = { ...response.user, ...superAdminData };
              console.log('SUCCESS: Super admin data fetched from database');
            }
          } else if (response.user.role === 'clinic_admin') {
            console.log('DEBUG: Attempting to fetch clinic data...');
            console.log('DEBUG: User ID:', response.user.id);
            console.log('DEBUG: User Email:', response.user.email);

            // Try to find clinic by ID first
            let clinicData = await DatabaseService.findById('clinics', response.user.id);

            // If not found by ID, try by email
            if (!clinicData) {
              console.log('WARNING: Clinic not found by ID, trying by email...');
              const clinicsByEmail = await DatabaseService.findBy('clinics', 'email', response.user.email);
              if (clinicsByEmail && clinicsByEmail.length > 0) {
                clinicData = clinicsByEmail[0];
                console.log('SUCCESS: Found clinic by email:', clinicData);
              }
            }

            console.log('CLINIC: Fetched clinic data from database:', clinicData);
            console.log(' Phone from database:', clinicData?.phone);
            console.log(' Address from database:', clinicData?.address);

            if (clinicData) {
              // Map contact_person to name and logo_url to avatar for the UI
              latestUserData = {
                ...response.user,
                ...clinicData,
                name: clinicData.contact_person || clinicData.name || response.user.name,
                avatar: clinicData.logo_url || clinicData.avatar || response.user.avatar
              };
              console.log('SUCCESS: Clinic admin data fetched from database');
              console.log('SUCCESS: Merged user data:', latestUserData);
              console.log('SUCCESS: Name field set from contact_person:', latestUserData.name);
              console.log('SUCCESS: Avatar field set from logo_url:', latestUserData.avatar ? 'Yes' : 'No');
            } else {
              console.warn('WARNING: No clinic data found for this user!');
            }
          } else if (response.user.role === 'patient') {
            console.log('DEBUG: Attempting to fetch patient data...');
            console.log('DEBUG: User ID:', response.user.id);
            console.log('DEBUG: User Email:', response.user.email);

            // Try to find patient by ID first
            let patientData = await DatabaseService.findById('patients', response.user.id);

            // If not found by ID, try by email
            if (!patientData) {
              console.log('WARNING: Patient not found by ID, trying by email...');
              const patientsByEmail = await DatabaseService.findBy('patients', 'email', response.user.email);
              if (patientsByEmail && patientsByEmail.length > 0) {
                patientData = patientsByEmail[0];
                console.log('SUCCESS: Found patient by email:', patientData);
              }
            }

            console.log(' Fetched patient data from database:', patientData);
            console.log('DEBUG: Patient avatar fields:', {
              avatar: patientData?.avatar,
              profile_image: patientData?.profile_image,
              profileImage: patientData?.profileImage,
              avatar_url: patientData?.avatar_url
            });

            if (patientData) {
              // Map avatar fields from database to user object
              const avatarUrl = patientData.avatar || patientData.profile_image || patientData.profileImage || patientData.avatar_url;

              latestUserData = {
                ...response.user,
                ...patientData,
                name: patientData.name || patientData.full_name || response.user.name,
                avatar: avatarUrl,
                profile_image: avatarUrl,
                profileImage: avatarUrl,
                avatar_url: avatarUrl
              };
              console.log('SUCCESS: Patient data fetched from database');
              console.log('SUCCESS: Merged user data:', latestUserData);
              console.log('SUCCESS: Avatar fields set:', {
                avatar: !!latestUserData.avatar,
                profile_image: !!latestUserData.profile_image,
                avatarUrl: avatarUrl
              });
            } else {
              console.warn('WARNING: No patient data found for this user!');
              console.log('INFO:  This might be a new patient, using API response data');
            }
          }
        } catch (dbError) {
          console.warn('WARNING: Failed to fetch latest user data from database, using API response:', dbError);
          // Continue with API response if database fetch fails
        }
        
        // Store the latest user data
        localStorage.setItem('user', JSON.stringify(latestUserData));
        
        // Set state
        setUser(latestUserData);
        setIsAuthenticated(true);
        
        console.log('SUCCESS: AuthContext: User data stored:', {
          name: latestUserData.name,
          role: latestUserData.role,
          clinicId: latestUserData.clinicId,
          hasAvatar: !!latestUserData.avatar
        });
        
        toast.success('Logged in successfully');
        console.log('SUCCESS: AuthContext: Login completed successfully');
        return { success: true, user: latestUserData };
      } else {
        console.log('ERROR: AuthContext: Invalid response format:', response);
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error('ALERT: AuthContext: Login failed:', error);
      toast.error(error.message || 'Login failed. Please try again.');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData, method = 'email') => {
    console.log('START: AuthContext: Starting registration process');

    // START: DEVELOPMENT MODE: Auto-succeed registration
    if (BYPASS_AUTH) {
      console.log('START: DEVELOPMENT MODE: Bypassing registration authentication');
      setLoading(true);

      // Simulate loading time
      await new Promise(resolve => setTimeout(resolve, 800));

      const defaultUser = {
        id: 'dev-user-' + Date.now(),
        name: userData.name || 'Development User',
        email: userData.email || 'dev@aims.com',
        role: userData.userType === 'super_admin' ? 'super_admin' :
              userData.userType === 'patient' ? 'patient' : 'clinic_admin',
        profilePicture: null,
        isActivated: true,
        clinicId: 'dev-clinic-123'
      };

      setUser(defaultUser);
      setIsAuthenticated(true);
      setLoading(false);
      localStorage.setItem('user', JSON.stringify(defaultUser));
      localStorage.setItem('authToken', 'dev-bypass-token');

      toast.success('START: Development mode registration successful!');
      console.log('SUCCESS: Development registration successful:', defaultUser.name, defaultUser.role);

      return {
        success: true,
        user: defaultUser,
        message: 'Development mode registration successful'
      };
    }

    try {
      setLoading(true);
      let response;
      
      console.log('EMAIL: Registration method:', method);
      console.log('NOTE: User data:', { 
        name: userData.name, 
        email: userData.email, 
        userType: userData.userType,
        hasPassword: !!userData.password,
        hasConfirmPassword: !!userData.confirmPassword 
      });
      
      switch (method) {
        case 'email':
          console.log('REFRESH: Calling authService.registerWithEmail...');
          response = await authService.registerWithEmail(userData);
          console.log(' AuthService response:', response);
          break;
        case 'google':
          response = await authService.registerWithGoogle();
          break;
        case 'github':
          response = await authService.registerWithGitHub();
          break;
        case 'facebook':
          response = await authService.registerWithFacebook();
          break;
        default:
          throw new Error('Invalid registration method');
      }

      console.log('SUCCESS: AuthContext: Registration response received', response);

      if (response && response.success) {
        if (response.needsActivation) {
          // Super admin needs activation - don't login automatically
          toast.success(response.message || 'Registration submitted for approval!');
          console.log('SUCCESS: AuthContext: Registration completed - needs activation');
          return { success: true, needsActivation: true };
        } else if (response.token) {
          // Normal registration with immediate login
          Cookies.set('authToken', response.token, { expires: 7 });
          localStorage.setItem('authToken', response.token);
          
          // Fetch the latest user data from database to get updated profile picture
          let latestUserData = response.user;
          try {
            console.log('REFRESH: Fetching latest user data from database after registration...');
            if (response.user.role === 'super_admin') {
              const superAdminData = await DatabaseService.findById('superAdmins', response.user.id);
              if (superAdminData) {
                latestUserData = { ...response.user, ...superAdminData };
                console.log('SUCCESS: Super admin data fetched from database');
              }
            } else if (response.user.role === 'clinic_admin') {
              console.log('DEBUG: Registration: Attempting to fetch clinic data...');
              console.log('DEBUG: Registration: User ID:', response.user.id);
              console.log('DEBUG: Registration: User Email:', response.user.email);

              // Try to find clinic by ID first
              let clinicData = await DatabaseService.findById('clinics', response.user.id);

              // If not found by ID, try by email
              if (!clinicData) {
                console.log('WARNING: Registration: Clinic not found by ID, trying by email...');
                const clinicsByEmail = await DatabaseService.findBy('clinics', 'email', response.user.email);
                if (clinicsByEmail && clinicsByEmail.length > 0) {
                  clinicData = clinicsByEmail[0];
                  console.log('SUCCESS: Registration: Found clinic by email:', clinicData);
                }
              }

              console.log('CLINIC: Registration: Fetched clinic data from database:', clinicData);
              console.log(' Registration: Phone from database:', clinicData?.phone);
              console.log(' Registration: Address from database:', clinicData?.address);

              if (clinicData) {
                // Map contact_person to name and logo_url to avatar for the UI
                latestUserData = {
                  ...response.user,
                  ...clinicData,
                  name: clinicData.contact_person || clinicData.name || response.user.name,
                  avatar: clinicData.logo_url || clinicData.avatar || response.user.avatar
                };
                console.log('SUCCESS: Clinic admin data fetched from database');
                console.log('SUCCESS: Registration: Merged user data:', latestUserData);
                console.log('SUCCESS: Registration: Name field set from contact_person:', latestUserData.name);
                console.log('SUCCESS: Registration: Avatar field set from logo_url:', latestUserData.avatar ? 'Yes' : 'No');
              } else {
                console.warn('WARNING: Registration: No clinic data found for this user!');
              }
            }
          } catch (dbError) {
            console.warn('WARNING: Failed to fetch latest user data from database, using API response:', dbError);
            // Continue with API response if database fetch fails
          }
          
          // Store the latest user data
          localStorage.setItem('user', JSON.stringify(latestUserData));
          
          setUser(latestUserData);
          setIsAuthenticated(true);
          
          console.log('SUCCESS: AuthContext: Registration user data stored:', {
            name: response.user.name,
            role: response.user.role,
            clinicId: response.user.clinicId
          });
          
          toast.success(response.message || 'Registration successful!');
          console.log('SUCCESS: AuthContext: Registration completed successfully');
          return { success: true };
        } else {
          console.log('ERROR: AuthContext: Registration success but no token provided');
          return { success: false, error: 'Registration completed but login failed' };
        }
      } else {
        // Handle case where response doesn't have success field or is falsy
        console.log('ERROR: AuthContext: Invalid registration response format:', response);
        const errorMessage = response?.error || response?.message || 'Registration failed with unknown error';
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log(' Logging out user...');

      // For development mode, we don't need to call authService
      if (!BYPASS_AUTH) {
        await authService.logout();
      }

      // Clear all authentication data
      if (typeof window !== 'undefined') {
        // Clear localStorage
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('demoUser');
        localStorage.removeItem('demoToken');

        // Clear all cached data
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('patients_') ||
              key.startsWith('patient_reports_') ||
              key.startsWith('dbCache_') ||
              key.startsWith('clinic_')) {
            localStorage.removeItem(key);
          }
        });
      }

      // Clear state
      setUser(null);
      setIsAuthenticated(false);

      toast.success('Logged out successfully');
      console.log('SUCCESS: Logout successful, redirecting to landing page');

      // Redirect to landing page after logout
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);

      // Still clear local state even if API call fails
      if (typeof window !== 'undefined') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('demoUser');
        localStorage.removeItem('demoToken');

        // Clear all cached data
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('patients_') ||
              key.startsWith('patient_reports_') ||
              key.startsWith('dbCache_') ||
              key.startsWith('clinic_')) {
            localStorage.removeItem(key);
          }
        });
      }

      setUser(null);
      setIsAuthenticated(false);

      // Still redirect to landing page
      window.location.href = '/';
    }
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      await authService.forgotPassword(email);
      toast.success('Password reset email sent!');
      return { success: true };
    } catch (error) {
      console.error('Forgot password failed:', error);
      toast.error(error.message || 'Failed to send reset email');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      setLoading(true);
      await authService.resetPassword(token, newPassword);
      toast.success('Password reset successful!');
      return { success: true };
    } catch (error) {
      console.error('Password reset failed:', error);
      toast.error(error.message || 'Password reset failed');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userData) => {
    try {
      setLoading(true);
      console.log('REFRESH: Updating user profile:', userData);

      // Update local state immediately for better UX
      const updatedUser = { ...user, ...userData };

      // If avatar is being updated, also add to profile_image and avatar_url for consistency
      if (userData.avatar) {
        updatedUser.profile_image = userData.avatar;
        updatedUser.avatar_url = userData.avatar;
        updatedUser.profileImage = userData.avatar;
        console.log('STORAGE: Avatar fields added to user object for consistency');
      }

      setUser(updatedUser);

      // Update localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      console.log('STORAGE: User object saved to localStorage:', updatedUser);

      // SUCCESS: CRITICAL: Update Supabase Auth password FIRST if password is being changed
      if (userData.password && supabase) {
        try {
          console.log('AUTH: Updating Supabase Auth password...');
          const { error: authError } = await supabase.auth.updateUser({
            password: userData.password
          });

          if (authError) {
            console.warn('WARNING: Supabase Auth password update failed:', authError.message);
            // Continue anyway to update local database
          } else {
            console.log('SUCCESS: Supabase Auth password updated successfully');
          }
        } catch (authError) {
          console.warn('WARNING: Failed to update Supabase Auth password:', authError);
          // Continue anyway to update local database
        }
      }

      // Save to database based on user role
      try {
        if (user?.role === 'super_admin') {
          // Update super admin in database
          await DatabaseService.update('superAdmins', user.id, userData);
          console.log('SUCCESS: Super admin profile saved to database');
        } else if (user?.role === 'clinic_admin') {
          // Map clinicName to name for database
          const clinicData = { ...userData };
          if (clinicData.clinicName) {
            clinicData.name = clinicData.clinicName;
            delete clinicData.clinicName;
          }
          // Map name (contact person) to contact_person for database
          if (clinicData.name && !clinicData.contact_person) {
            clinicData.contact_person = clinicData.name;
          }
          // Map avatar to logo_url (clinics table doesn't have avatar field)
          if (clinicData.avatar) {
            clinicData.logo_url = clinicData.avatar;
            console.log('STORAGE: Avatar mapped to logo_url for clinics table');
            console.log('STORAGE: Avatar size:', clinicData.avatar?.length || 0, 'characters');
            delete clinicData.avatar;
          }
          console.log('NOTE: Original userData received:', userData);
          console.log('NOTE: Mapped clinic data for database:', clinicData);
          console.log('NOTE: contact_person field:', clinicData.contact_person);
          console.log('NOTE: logo_url field present:', !!clinicData.logo_url);
          console.log('NOTE: User ID for update:', user.id);
          // Update clinic admin in database
          const updateResult = await DatabaseService.update('clinics', user.id, clinicData);
          console.log('SUCCESS: Clinic admin profile saved to database');
          console.log('SUCCESS: Update result:', updateResult);
        } else {
          // For regular users (patients), find patient record and update
          console.log('INFO: Updating patient profile...');
          console.log('INFO: User email:', user.email);

          // Find patient record by email
          const allPatients = await DatabaseService.get('patients');
          const userEmailLower = user.email.trim().toLowerCase();
          const patientRecord = allPatients.find(p => {
            if (!p.email) return false;
            return p.email.trim().toLowerCase() === userEmailLower;
          });

          if (!patientRecord) {
            console.error('ERROR: Patient record not found for email:', user.email);
            throw new Error('Patient record not found');
          }

          console.log('INFO: Found patient record ID:', patientRecord.id);
          console.log('INFO: Patient record email:', patientRecord.email);

          // Map avatar field to database fields
          const patientData = { ...userData };
          console.log('DEBUG: Original userData:', userData);
          console.log('DEBUG: userData.avatar:', userData.avatar);

          if (patientData.avatar) {
            // Map avatar to both profile_image and avatar_url for compatibility
            patientData.profile_image = patientData.avatar;
            patientData.avatar_url = patientData.avatar;
            patientData.profileImage = patientData.avatar;
            console.log('STORAGE: Avatar mapped to profile_image/avatar_url for patients table');
            console.log('STORAGE: Avatar URL being saved:', patientData.avatar);
            console.log('STORAGE: profile_image field:', patientData.profile_image);
            console.log('STORAGE: avatar_url field:', patientData.avatar_url);
            // Don't save the original avatar field to database
            delete patientData.avatar;
          } else {
            console.log('WARNING: No avatar in userData to save');
          }

          // Remove password fields from patient data (handled separately by Supabase Auth)
          delete patientData.password;
          delete patientData.currentPassword;
          delete patientData.confirmPassword;

          console.log('DATABASE: About to save patientData:', patientData);
          console.log('DATABASE: Saving to patients table, record ID:', patientRecord.id);
          console.log('DATABASE: Fields being updated:', Object.keys(patientData));

          // Update patient record in database
          const updateResult = await DatabaseService.update('patients', patientRecord.id, patientData);
          console.log('SUCCESS: Patient profile saved to database');
          console.log('SUCCESS: Update result:', updateResult);
          console.log('SUCCESS: Verify - profile_image saved:', !!patientData.profile_image);
          console.log('SUCCESS: Verify - avatar_url saved:', !!patientData.avatar_url);
        }
      } catch (dbError) {
        console.error('ERROR: Database update failed:', dbError);
        console.warn('WARNING: Failed to save to database, but local update successful:', dbError);
        // Don't fail the entire operation if database fails
      }
      
      toast.success('Profile updated successfully!');
      console.log('SUCCESS: Profile updated successfully');
      return { success: true };
    } catch (error) {
      console.error('Profile update failed:', error);
      toast.error(error.message || 'Failed to update profile');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateUser,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
